import React, { useContext, useEffect, useState } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table/interface";
import Course from "../../interfaces/course.interface";
import {
  cloneCourse,
  getCoursesByInstitution,
  getCoursesByStudent,
  getCoursesByStaff,
} from "../../services/course.service";
import { LIMIT } from "../../constants/pagination.constant";
import Loading from "../shared/loading/loading";
import { useNavigate } from "react-router-dom";
import { CloudDownloadOutlined } from "@ant-design/icons";
import CourseStaff from "../../interfaces/course-staff.interface";
import { useRole } from "../../hooks/user-roles";
import { UserContext } from "../../context/user.context";
import { PaginateCourseByUser } from "../../interfaces/pagination.interface";

interface StudentsCourses {
  id?: number;
  courseId?: number;
  userId?: number;
  courseCostId?: number;
  invoiceId?: number;
  status?: string;
  createdBy?: string;
  waitlist?: number;
  updatedAt?: string;
  registeredAt?: string;
  createdAt?: string;
  deletedAt?: string;
}

interface DataType extends Course {
  studentsCourses?: StudentsCourses[];
  key: React.Key;
  coursesStaff?: CourseStaff[];
}

interface TableSorterConfig {
  orderBy?: string;
  sort?: string;
}

interface CourseTablePros {
  query?: string;
  handleChange: string;
  listType: string;
}

const CoursesTable = ({ query, handleChange, listType }: CourseTablePros) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<DataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useContext(UserContext);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string | undefined>();
  const [sorter, setSorter] = useState<TableSorterConfig>({
    orderBy: "id",
    sort: "DESC",
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: LIMIT,
  });
  const { isStaff, isStudent, isSuperAdmin } = useRole();

  const columns: ColumnsType<DataType> = [
    {
      key: "type",
      title: "Type",
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.type}
        </div>
      ),
    },
    {
      key: "year",
      title: "Year",
      sorter: (a, b) => Number(a.year) - Number(b.year),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.year}
        </div>
      ),
    },
    {
      key: "term",
      title: "Term",
      sorter: (a, b) => a.term.localeCompare(b.term),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.term}
        </div>
      ),
    },
    {
      key: "code",
      title: "Code",
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.code}
        </div>
      ),
    },
    {
      key: "name",
      title: "Name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.name}
        </div>
      ),
    },
  ];

  if (listType === "my" && isStudent) {
    columns.push(
      {
        title: "My Status",
        render: (_: DataType) => (
          <div className="cursor-pointer" onClick={() => courseSelected(_)}>
            {_.studentsCourses?.[0].status}
          </div>
        ),
      },
      {
        title: "Type of registration",
        render: (_: DataType) => (
          <div className="cursor-pointer" onClick={() => courseSelected(_)}>
            {_.costs?.[0]?.registrationType}
          </div>
        ),
      },
    );
  } else if (listType === "my" && isStaff) {
    columns.push({
      title: "Role",
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.coursesStaff?.[0].role}
        </div>
      ),
    });
  } else {
    columns.push({
      title: "Course Status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (_: DataType) => (
        <div className="cursor-pointer" onClick={() => courseSelected(_)}>
          {_.status}
        </div>
      ),
    });
  }

  if (isSuperAdmin) {
    columns.push({
      title: "Action",
      render: (_: DataType) => (
        <div
          className="flex justify-center"
          style={{ cursor: "pointer" }}
          onClick={() => cloneElement(_)}
        >
          <Tooltip title={"Import " + _.type}>
            <CloudDownloadOutlined
              style={{ fontSize: "20px", color: "#5ead8e" }}
            />
          </Tooltip>
        </div>
      ),
    });
  }

  const courseSelected = (course: DataType) => {
    const courseURL =
      listType === "my" ? `/SEABC/courses/${course.id}` : `${course.id}`;
    navigate(courseURL);
  };

  const cloneElement = async (course: DataType) => {
    const res = await cloneCourse(course.id);
    navigate(`${res.id}-clone`);
  };

  const getCoursesByCondition = async () => {
    const limit = pagination?.pageSize ?? LIMIT;
    const skip = ((pagination?.current ?? 1) - 1) * limit;
    let response: { count: number; list: Course[] };
    const params: PaginateCourseByUser = {
      ...(((listType === "my" && isStudent) ||
        (listType === "my" && isStaff)) && { userId: user?.id! }),
      institutionId: user?.institutionId!,
      limit,
      skip,
      query,
      type: handleChange,
      ...(sorter?.orderBy && {
        orderBy: sorter.orderBy,
      }),
      ...(sorter?.sort && { sort: sorter.sort }),
    };
    if (listType === "my" && isStudent) {
      response = await getCoursesByStudent(params);
    } else if (listType === "my" && isStaff) {
      response = await getCoursesByStaff(params);
    } else {
      delete params.userId;
      params.institutionId = window.location.href.split("/")[3];
      response = await getCoursesByInstitution(params);
    }

    if (response) {
      setCourses(response.list.map((c) => ({ ...c, key: c.id })));
      setTotal(response.count);
      setLoading(false);
    }
  };

  useEffect(() => {
    getCoursesByCondition();
  }, [user, pagination?.current, handleChange, listType, search, sorter]);

  useEffect(() => {
    if (query !== undefined) {
      setPagination((state) => ({
        ...state,
        current: 1,
      }));
      setSearch(query);
    }
  }, [query]);

  const generateSort = (key: string, order: string) => {
    if (key === sorter?.orderBy) {
      if (order === "descend") return false;
    }

    return true;
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter: any,
  ) => {
    setSorter({
      ...(generateSort(sorter.columnKey, sorter.order) && {
        orderBy: sorter.columnKey,
      }),
      ...(generateSort(sorter.columnKey, sorter.order) && {
        sort: sorter.order === "ascend" ? "ASC" : "DESC",
      }),
    });
    setPagination(pagination);
  };

  if (loading) {
    return <Loading></Loading>;
  }

  if (courses.length) {
    return (
      <>
        <Table
          columns={columns}
          dataSource={courses}
          bordered={true}
          onChange={(_pagination, _, sorter) =>
            handleTableChange(_pagination, sorter)
          }
          pagination={{ ...pagination, total }}
        ></Table>
      </>
    );
  }

  return (
    <div className="not-found">
      {listType === "my"
        ? isStaff
          ? "You have not yet being assigned to any course"
          : "You have not yet enrolled in a course"
        : "There are no courses for this institution"}
    </div>
  );
};

export default CoursesTable;
